import { getCbamReportingPeriod } from '@/config/cbamReportingPeriods'
import { useCompleteCbamReport } from '@/features/cbam-reporting/hooks/cbamReports'
import useI18n from '@/shared/hooks/usei18n'
import { Button } from '@/common/components/Button/Button'
import { GenericDialog } from '@/common/components/GenericDialog/GenericDialog'
import { TriangleExclamationIcon } from '@/common/components/icons'
import toast from 'react-hot-toast'

export default function ConfirmCompleteReportDialog({
  isOpen,
  close,
  organizationId,
  cbamReportingId,
  periodId
}: {
  isOpen: boolean
  close: () => void
  organizationId: string
  cbamReportingId: string
  periodId: string
}) {
  const { t } = useI18n()

  const { mutateAsync: completeCbamReport, isPending: isCompletingCbamReport } =
    useCompleteCbamReport(organizationId, cbamReportingId)

  const onCompleteCbamReport = async () => {
    const period = getCbamReportingPeriod(periodId)
    if (!period?.year || !period?.quarter) {
      return
    }

    try {
      await completeCbamReport({ year: period.year, quarter: period.quarter })
      toast.success(t('cbamReporting:cbamReportDetails.notification.completeReportSuccess'), {
        position: 'top-right'
      })
      close()
    } catch {
      toast.error(t('cbamReporting:cbamReportDetails.notification.completeReportError'), {
        position: 'top-right'
      })
    }
  }

  return (
    <GenericDialog
      isOpen={isOpen}
      close={close}
      title={t('cbamReporting:cbamReportDetails.confirmCompleteReportDialog.title')}
      size="2xl"
      icon={<TriangleExclamationIcon className="size-6" />}
    >
      <div className="mb-6">
        <p>{t('cbamReporting:cbamReportDetails.confirmCompleteReportDialog.description')}</p>
      </div>
      <footer className="flex justify-end gap-2">
        <Button variant="subtle" onClick={close}>
          {t('common:actions.cancel')}
        </Button>
        <Button
          disabled={isCompletingCbamReport}
          isLoading={isCompletingCbamReport}
          onClick={onCompleteCbamReport}
        >
          {t('common:actions.confirm')}
        </Button>
      </footer>
    </GenericDialog>
  )
}
